import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FilterableMultiSelect } from "@carbon/react";
import { useUser } from "../../context";
import { useUsersByCompany } from "../../hooks/useUsers";

type OwnerOption = { id: string; label: string };

type Props = {
  id: string;
  /** Selected owner user ids (the unified list defaults these to the current user). */
  selectedOwnerIds: string[];
  onChange: (ownerIds: string[]) => void;
};

/**
 * Owner (responsible-user) multiselect for the unified materials/orders lists: pick one or more
 * company users to narrow the list, or clear it to see everyone. The current user is always first
 * (tagged "you") — `useUsersByCompany` filters the caller out of its roster, so we add them back
 * here so the default "just me" selection has a matching option.
 */
const OwnerFilter = ({ id, selectedOwnerIds, onChange }: Props) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { data: otherUsers } = useUsersByCompany(user?.id || "");

  const options = useMemo<OwnerOption[]>(() => {
    const meName =
      user?.fullName ||
      `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
    const me: OwnerOption[] = user?.id
      ? [{ id: user.id, label: `${meName} (${t("owner.you")})` }]
      : [];
    const others: OwnerOption[] = (otherUsers ?? []).map((u: any) => ({
      id: u.id,
      label: u.fullName || `${u.firstName} ${u.lastName}`,
    }));
    return [...me, ...others];
  }, [user, otherUsers, t]);

  const selectedItems = options.filter((o) => selectedOwnerIds.includes(o.id));

  return (
    <FilterableMultiSelect
      id={id}
      titleText={t("owner.filterLabel")}
      placeholder={t("owner.filterPlaceholder")}
      items={options}
      itemToString={(o) => (o as OwnerOption)?.label ?? ""}
      selectedItems={selectedItems}
      onChange={({ selectedItems: sel }) =>
        onChange(((sel ?? []) as OwnerOption[]).map((o) => o.id))
      }
    />
  );
};

export default OwnerFilter;
